import * as THREE from 'three';
import Firework from '../entities/Firework.js';
import ExplosionPatterns from '../engine/ExplosionPatterns.js';

/**
 * FireworkFactory类 - 创建和配置烟花实例
 * 
 * 职责:
 * - 创建烟花实例
 * - 随机选择爆炸形状
 * - 随机生成颜色方案
 * - 配置烟花参数
 * 
 * 验证需求: Requirements 6.5
 */
class FireworkFactory {
  constructor(scene, particleEngine) {
    if (!scene) {
      throw new Error('Scene is required');
    }
    if (!particleEngine) {
      throw new Error('ParticleEngine is required');
    }

    this.scene = scene;
    this.particleEngine = particleEngine;

    // 预定义颜色方案
    this.colorSchemes = [
      // 红色/金色
      {
        inner: new THREE.Color(1.0, 0.3, 0.1),
        outer: new THREE.Color(1.0, 0.8, 0.2)
      },
      // 蓝色/白色
      {
        inner: new THREE.Color(0.2, 0.5, 1.0),
        outer: new THREE.Color(0.8, 0.9, 1.0)
      },
      // 绿色/黄色
      {
        inner: new THREE.Color(0.2, 1.0, 0.3),
        outer: new THREE.Color(0.8, 1.0, 0.4)
      },
      // 紫色/粉色
      {
        inner: new THREE.Color(0.6, 0.2, 1.0),
        outer: new THREE.Color(1.0, 0.4, 0.8)
      },
      // 橙色/黄色
      {
        inner: new THREE.Color(1.0, 0.5, 0.0),
        outer: new THREE.Color(1.0, 1.0, 0.3)
      },
      // 青色/蓝色
      {
        inner: new THREE.Color(0.0, 0.8, 1.0),
        outer: new THREE.Color(0.3, 0.6, 1.0)
      },
      // 白色/银色
      {
        inner: new THREE.Color(1.0, 1.0, 1.0),
        outer: new THREE.Color(0.7, 0.8, 0.9)
      },
      // 粉色/红色
      {
        inner: new THREE.Color(1.0, 0.2, 0.5),
        outer: new THREE.Color(1.0, 0.5, 0.3)
      }
    ];
  }

  /**
   * 创建烟花实例
   * @param {Object} customConfig - 自定义配置（可选）
   * @returns {Firework}
   */
  createFirework(customConfig = {}) {
    // 随机发射位置（场景底部，X坐标随机）
    const launchPosition = new THREE.Vector3(
      (Math.random() - 0.5) * 20, // -10 到 10
      0,
      (Math.random() - 0.5) * 10  // -5 到 5
    );

    // 随机发射角度
    // 垂直方向（80-100度）或斜向（60-80度）
    const isVertical = Math.random() > 0.5;
    const launchAngle = isVertical
      ? 80 + Math.random() * 20  // 80-100度
      : 60 + Math.random() * 20; // 60-80度

    // 随机爆炸高度（15-25单位）
    const explosionHeight = 15 + Math.random() * 10;

    // 随机爆炸形状
    const explosionPattern = this.getRandomExplosionPattern();

    // 根据形状调整粒子数量
    const particleCount = this._getParticleCountForPattern(explosionPattern);

    // 随机颜色方案
    const colors = this.getRandomColor();

    // 合并配置
    const config = {
      scene: this.scene,
      particleEngine: this.particleEngine,
      launchPosition: launchPosition,
      launchAngle: launchAngle,
      launchSpeed: 18 + Math.random() * 6, // 18-24
      explosionHeight: explosionHeight,
      explosionPattern: explosionPattern,
      particleCount: particleCount,
      particleSpeed: 7 + Math.random() * 4, // 7-11
      innerColor: colors.inner,
      outerColor: colors.outer,
      hasTrail: true,
      trailLength: 15 + Math.floor(Math.random() * 10), // 15-25
      ...customConfig
    };

    return new Firework(config);
  }

  /**
   * 随机选择爆炸形状
   * @returns {string}
   */
  getRandomExplosionPattern() {
    return ExplosionPatterns.getRandomPattern();
  }

  /**
   * 随机选择颜色方案
   * @returns {Object} {inner: THREE.Color, outer: THREE.Color}
   */
  getRandomColor() {
    const scheme = this.colorSchemes[Math.floor(Math.random() * this.colorSchemes.length)];
    return {
      inner: scheme.inner.clone(),
      outer: scheme.outer.clone()
    };
  }

  /**
   * 根据形状获取推荐的粒子数量
   * @param {string} pattern - 形状名称
   * @returns {number}
   * @private
   */
  _getParticleCountForPattern(pattern) {
    switch (pattern) {
      case 'sphere':
        return 800 + Math.floor(Math.random() * 400); // 800-1200
      case 'ring':
        return 600 + Math.floor(Math.random() * 400); // 600-1000
      case 'willow':
        return 1000 + Math.floor(Math.random() * 500); // 1000-1500
      case 'palm':
        return 800 + Math.floor(Math.random() * 400); // 800-1200
      default:
        return 1000;
    }
  }

  /**
   * 创建指定形状的烟花
   * @param {string} pattern - 形状名称
   * @returns {Firework}
   */
  createFireworkWithPattern(pattern) {
    return this.createFirework({ explosionPattern: pattern });
  }

  /**
   * 创建指定颜色的烟花
   * @param {THREE.Color} innerColor - 内层颜色
   * @param {THREE.Color} outerColor - 外层颜色
   * @returns {Firework}
   */
  createFireworkWithColors(innerColor, outerColor) {
    return this.createFirework({
      innerColor: innerColor,
      outerColor: outerColor
    });
  }

  /**
   * 批量创建烟花
   * @param {number} count - 数量
   * @returns {Firework[]}
   */
  createMultiple(count) {
    const fireworks = [];
    for (let i = 0; i < count; i++) {
      fireworks.push(this.createFirework());
    }
    return fireworks;
  }
}

export default FireworkFactory;
